"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.BaseMapSourceModule = void 0;

var _utils = require("../utils");

var __extends = void 0 && (void 0).__extends || function () {
  var extendStatics = function (d, b) {
    extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    };

    return extendStatics(d, b);
  };

  return function (d, b) {
    extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

/**
 * A map source module is a container for providing all of the required configuration and
 * functionality needed for rendering and controlling a map content source on an interactive map
 * application.
 *
 * A module can be a direct instance of `MapSourceModule`, in which case a configuration object
 * must be provided when instantiating the instance. Or, a module can be provided as a subclass
 * of `MapSourceModule`, in which case the subclass can override the necessary properties and
 * methods of the parent class to provide the necessary configuration and functionality.
 *
 * Regardless which implementation method is used, a module must return a valid map content
 * source from the `source()` method.
 *
 * @class MapSourceModule
 * @implements {IMapSourceModule}
 */
var MapSourceModule =
/** @class */
function () {
  /**
   * Initializes and returns a map source module instance configured with the specified options
   * if provided.
   *
   * @param {any} [opts=null]
   * @memberof MapSourceModule
   */
  function MapSourceModule(opts) {
    if (opts === void 0) {
      opts = null;
    }

    this._opts = opts || {};
    this._account = (0, _utils.get)(opts, 'account');
  }

  Object.defineProperty(MapSourceModule.prototype, "id", {
    /**
     * Module identifier.
     *
     * @readonly
     * @type {string}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._opts.id;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "account", {
    /**
     * Aeris account associated with the module.
     *
     * @readonly
     * @type {Account}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._account;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "app", {
    /**
     * Application the module was initialized with.
     *
     * @readonly
     * @type {InteractiveMapApp}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._app;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "map", {
    /**
     * Application map instance.
     *
     * @readonly
     * @type {InteractiveMap}
     * @memberof MapSourceModule
     */
    get: function () {
      return (0, _utils.isset)(this._app) ? this._app.map : this._map;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "dataSource", {
    /**
     * Map data source created for the module.
     *
     * @type {*}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._source;
    },

    /**
     * Sets the module's map data source.
     *
     * @memberof MapSourceModule
     */
    set: function (value) {
      var _this = this;

      this._source = value;

      if (value) {
        value.on('data:load', function (e) {
          var results = e.data.results;

          if (results) {
            _this.onData(results);
          }
        });
      }
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "data", {
    /**
     * Data associated with the module.
     *
     * If the module loads remote data when added to a map, this value will return the data that
     * was last returned by the remote request.
     *
     * @readonly
     * @type {*}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._data;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "opts", {
    /**
     * Returns the module's configuration options.
     */
    get: function () {
      return this._opts;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(MapSourceModule.prototype, "group", {
    /**
     * Parent module group the module belongs to, if any.
     *
     * @type {ModuleGroup}
     * @memberof MapSourceModule
     */
    get: function () {
      return this._group;
    },

    /**
     * Sets the parent module group.
     *
     * @memberof MapSourceModule
     */
    set: function (group) {
      this._group = group;
      this._opts.controls = null;
    },
    enumerable: false,
    configurable: true
  });
  /**
   * Initializes the module with the specified Aeris account and application.
   *
   * @param {Account} account
   * @param {InteractiveMapApp} app
   * @memberof MapSourceModule
   */

  MapSourceModule.prototype.initialize = function (account, app, map) {
    if (map === void 0) {
      map = null;
    }

    this._account = account;
    this._app = app;
    this._map = app ? app.map : map;
  };
  /**
   * Returns the map content source configuration.
   *
   * @see LayerOptions
   * @returns {*}
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.source = function () {
    return this._opts.source;
  };
  /**
   * Returns the application controls configuration.
   *
   * @see ButtonOptions
   * @see SegmentedButtonOptions
   * @see ButtonGroupOptions
   * @returns {*}
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.controls = function () {
    return this._opts.controls;
  };
  /**
   * Returns the legend configuration.
   *
   * @see LegendOptions
   * @returns {*}
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.legend = function () {
    return this._opts.legend;
  };
  /**
   * Returns the configuration for the info panel content view.
   *
   * @see InfoPanelContentViewOptions
   * @returns {*}
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.infopanel = function () {
    return this._opts.infopanel;
  };
  /**
   * Shows the map info panel using the specified title and data for the configured view
   * associated with the module.
   *
   * @param {string} title
   * @param {*} [data]
   * @returns {InfoPanel}
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.showInfoPanel = function (title, data) {
    return this.app.showInfo(this.id, title, data);
  };
  /**
   * Called when the module has been initialized with an application.
   *
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onInit = function () {
    if (this._opts.onInit) {
      this._opts.onInit();
    }
  };
  /**
   * Called when the module's map content source has been added to the map.
   *
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onAdd = function () {
    if (this._opts.onAdd) {
      this._opts.onAdd();
    }
  };
  /**
   * Called when the module's map content source is removed from the map.
   *
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onRemove = function () {
    if (this._opts.onRemove) {
      this._opts.onRemove();
    }
  };
  /**
   * Called when the module's map content source has loaded data.
   *
   * @param {*} result
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onData = function (result) {
    if (this._opts.onData) {
      this._opts.onData(result);
    }
  };
  /**
   * Called when a marker associated with the module's map content source is clicked on the map.
   *
   * @param {*} marker
   * @param {*} data
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onMarkerClick = function (marker, data) {
    if (this._opts.onMarkerClick) {
      this._opts.onMarkerClick(marker, data);
    }
  };
  /**
   * Called when a polygon or polyline associated with the module's map content source is clicked
   * on the map.
   *
   * @param {*} shape
   * @param {*} data
   * @memberof MapSourceModule
   */


  MapSourceModule.prototype.onShapeClick = function (shape, data) {
    if (this._opts.onShapeClick) {
      this._opts.onShapeClick(shape, data);
    }
  };

  return MapSourceModule;
}();

var BaseMapSourceModule =
/** @class */
function (_super) {
  __extends(BaseMapSourceModule, _super);

  function BaseMapSourceModule() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  return BaseMapSourceModule;
}(MapSourceModule);

exports.BaseMapSourceModule = BaseMapSourceModule;
var _default = MapSourceModule;
exports.default = _default;